import { useEffect, useState } from 'react'

// The movie projection, as one screen sees it. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT CANNOT THROW, AND THE PAGE MUST RENDER WITHOUT IT
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §7 item 5, written about the directory and true of every projection:
// *"Kill R2: pages render with placeholder names and everything else intact —
// names are decoration under the same rule as enrichment, and their absence is
// labeled, not blank."*
//
// The portal home is identity, releases, trips, the branch selector and this.
// Kill the projection and four of those five are untouched — so `fetchMovie` has
// NO rejecting branch: offline, 401, 500, 503, a body that is not JSON, no
// Functions at all under `vite dev` — every one of them resolves to
// `unavailable`, which renders a sentence in the panel and nothing else.
//
// ⚠️ THAT IS STRONGER THAN `trips/api.js`'s `BoardError`, and deliberately, for
// the reason `src/directory.js` writes down: the board's failure IS the screen's
// failure and this one must never be. It also means no caller needs a `catch`,
// and no caller can get it wrong by forgetting one.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THREE STATES, AND `loading` IS NOT `unavailable`
// ═══════════════════════════════════════════════════════════════════════════
//
// `src/directory.js`'s lesson, and it applies harder here because this panel is
// the page: for the first moment of every load NOTHING HAS FAILED. A panel that
// said "movie night is unavailable" there would be the portal reporting an
// outage that has not happened, on the surface Jordan asked to *"look really
// good"*.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SENTENCES ARE OURS, NOT THE FUNCTION'S
// ═══════════════════════════════════════════════════════════════════════════
//
// `functions/api/movie.js` answers with a real sentence and a real status, and
// this file reads NEITHER onto the page. Same choice `src/directory.js` made:
// the states a SCREEN has are fewer than the states a Function has, the body of
// a 500 from a proxy in front of us is not something we wrote, and a client that
// renders whatever text arrived is one bad upstream away from putting somebody
// else's error prose in front of a member. The Function's message is for the
// operator reading a network tab; these are for the person.

export const MOVIE_ENDPOINT = '/api/movie'

export const LOADING = 'loading'
export const READY = 'ready'
export const UNAVAILABLE = 'unavailable'

/** Why your own half is missing, in words. A closed table, keyed by the reason
 * `functions/_lib/projections.js` hands back.
 *
 * 🔴 `null` IS NOT IN HERE AND MUST NOT BE. A `mineFailure` of `null` means the
 * store genuinely knows nothing about this member — the designed new-member
 * state, not a fault — and giving it a sentence here is how a beautiful empty
 * state starts being shown to people who have six years of history. The two
 * absences are different facts and the page says which one it is looking at. */
export const MINE_FAILURE_NOTE = Object.freeze({
  unreachable: 'Your own record could not be read just now. This is not about your account.',
  unreadable: 'Your own record could not be read just now. This is not about your account.',
  unsupported:
    'Your own record is published in a newer format than this page reads, so it is not shown.',
  unbound: 'Your own record is not configured on this deployment.',
  unaddressable: 'Your own record could not be addressed on this deployment.',
})

/** Nobody has answered yet. Renders nothing, because nothing has failed. */
export function loadingMovie() {
  return { state: LOADING, shared: null, mine: null, mineFailure: null }
}

/** It did not load. Renders one sentence, and the rest of the page is intact. */
export function unavailableMovie() {
  return { state: UNAVAILABLE, shared: null, mine: null, mineFailure: null }
}

/**
 * The Function's answer, checked at the seam.
 *
 * ⚠️ THE SHAPE IS CHECKED HERE TOO, and it is not a duplicate of the Function's
 * check. `functions/_lib/projections.js` asserts what came out of the BUCKET;
 * this asserts what came over the WIRE, and those are two different edges with
 * a proxy, a service worker and a browser cache in between. The failure they
 * guard against is the same though, so the answer is the same: an answer we
 * cannot read is `unavailable`, never a half-rendered panel.
 */
export function buildMovie(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return unavailableMovie()
  const shared = payload.shared
  if (!shared || typeof shared !== 'object' || Array.isArray(shared)) return unavailableMovie()
  const mine =
    payload.mine && typeof payload.mine === 'object' && !Array.isArray(payload.mine)
      ? payload.mine
      : null
  return {
    state: READY,
    shared,
    mine,
    // Only a reason we have a sentence for. Anything else is treated as no
    // sentence rather than rendered raw — see the header.
    mineFailure:
      typeof payload.mineFailure === 'string' &&
      Object.hasOwn(MINE_FAILURE_NOTE, payload.mineFailure)
        ? payload.mineFailure
        : null,
  }
}

/**
 * Ask the Function. Resolves to a movie state, always — see the header.
 *
 * ⚠️ `credentials: 'same-origin'`: the session cookie IS the caller's identity,
 * and it is the ONLY thing that decides which 3b object comes back. A fetch that
 * dropped it would be answered 401 and reported here as "unavailable" — the
 * portal blank for everybody, from one absent option.
 */
export async function fetchMovie(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return unavailableMovie()
  try {
    const response = await fetchImpl(MOVIE_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return unavailableMovie()
    return buildMovie(await response.json())
  } catch {
    return unavailableMovie()
  }
}

/**
 * The movie projection for one screen. Starts `loading`, lands `ready` or
 * `unavailable`.
 *
 * ⚠️ ONE REQUEST PER SCREEN AND NO MODULE-LEVEL CACHE — the same trade
 * `session.js` and `directory.js` both made, and for the reason they wrote down:
 * a promise cache saves a request and buys shared mutable state every test has
 * to reset. One surface asks for this today.
 */
export function useMovie() {
  const [movie, setMovie] = useState(loadingMovie)

  useEffect(() => {
    let cancelled = false
    // No `.catch`: `fetchMovie` has no rejecting branch, and a handler that can
    // never run reads as if it could.
    fetchMovie().then((answer) => {
      if (!cancelled) setMovie(answer)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return movie
}

// ═══════════════════════════════════════════════════════════════════════════
// READING THE BLOBS — one place, so two components cannot disagree
// ═══════════════════════════════════════════════════════════════════════════

/** Every night the shared blob carries, newest first, defensively. */
export function nightsOf(shared) {
  const nights = shared?.nights
  return Array.isArray(nights) ? nights.filter((n) => n && typeof n === 'object') : []
}

/**
 * The night that has been picked and not yet played, or `null`.
 *
 * 🔴 MEASURED IN THE STORE RATHER THAN INFERRED FROM POSITION.
 * `MovieNightManager.record_movie_pick` APPENDS TO HISTORY THE MOMENT A PICK IS
 * MADE — before the film is watched, before it is finalized, before there is any
 * rating — so `nights[0]` is not "last night", it is "the newest row", and those
 * are different things on every day of the week except one.
 *
 * `finalized_at` is what tells them apart: it is written by whichever of
 * `finalize_movie_night` / `record_retro_night` closed the night, and it is
 * absent on a night that has not been closed. So an unfinalized newest row is
 * THIS WEEK'S PICK, and the panel leads with it.
 *
 * ⚠️ AND THE PANEL DOES NOT CALL IT "TONIGHT". The design's frame says
 * "Tonight · Thu"; the day and the time come from the guild's
 * `movie_night_day` and its settings, and NEITHER IS PUBLISHED. Naming a day we
 * cannot read would be the page inventing the one fact a member would check it
 * against.
 */
export function upcomingNight(shared) {
  const [newest] = nightsOf(shared)
  if (!newest) return null
  return newest.finalized_at ? null : newest
}

/**
 * The most recent night the room actually returned a verdict on, or `null`.
 *
 * Not `nights[0]`, and not "the newest finalized one" either: a night can be
 * finalized with no poll result at all, and *"a tally with no votes is not a
 * verdict"* is the publisher's own rule — it returns `rating: null` for those.
 * So this asks the only question the block is about: which night has a verdict.
 */
export function lastVerdictNight(shared) {
  return nightsOf(shared).find((night) => night.rating && typeof night.rating === 'object') ?? null
}

/** The newest crowned Movie of the Year, or `null`. Published newest first. */
export function latestChampion(shared) {
  const moty = shared?.moty
  if (!Array.isArray(moty)) return null
  return moty.find((season) => season && typeof season === 'object') ?? null
}

/**
 * Everybody in the rotation, in schedule order, or `[]`. P6-171.
 *
 * 🔴 `rotation` IS ABSENT FROM EVERY BLOB PUBLISHED BEFORE P6-165, AND THE
 * PANEL HAS TO KEEP WORKING ON ONE. The projection in the bucket right now was
 * written by P6-155 and carries no `rotation` key at all; it is replaced when
 * the bot next publishes, and "next" is a process we do not control from here.
 * So this returns `[]` for that blob and the strip falls back to what P6-83
 * could already source — see RotationStrip.
 *
 * ⚠️ Sorted by `position` even though `utils/projection_publisher.py` already
 * emits them sorted. That is NOT a second derivation of the order — it is the
 * same key the publisher sorted on, so the two cannot disagree — it is what
 * makes "schedule order" true by construction here rather than true by
 * trusting an array's order across a Function, a proxy and a JSON round trip.
 */
export function rotationSlots(shared) {
  const participants = shared?.rotation?.participants
  if (!Array.isArray(participants)) return []
  return participants
    .filter(
      (p) => p && typeof p === 'object' && typeof p.user_id === 'string' && Number.isInteger(p.position),
    )
    .sort((a, b) => a.position - b.position)
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ONE FACT, ONE SPELLING (P6-177)
// ═══════════════════════════════════════════════════════════════════════════
//
// The portal shipped `rotation_position` in three strings and spelled it two
// ways on ONE screen: `In the rotation · slot 4` under the reader's name, and
// `Rotation · you are 4th` over the disc strip four hundred pixels below it.
// Same number, same person, same load. Nothing was wrong and it read as two
// different facts, which is the whole of the row.
//
// ⚠️ THE FIX IS THE FUNCTION, NOT THE EDIT. Rewriting the ordinal into a slot
// would have left three literals that can diverge again on the next change;
// this leaves one noun and one shape, and `portal.test.jsx` fails if a fourth
// spelling appears. `slot` wins over the ordinal because the discs are numbered
// and their own screen-reader copy already said `Slot 4: Sam` — two of the
// three sites agreed, so the odd one moved.

/** The one word for a member's place in the rotation. Never inline it. */
const SLOT_NOUN = 'slot'

/**
 * `slot 4`, or `Slot 4` at the start of a sentence.
 *
 * The capital is derived rather than typed for the reason above: a second
 * literal is a second thing to keep in step, and this way the noun exists
 * exactly once in the codebase.
 */
export function slotLabel(position, { capitalized = false } = {}) {
  const noun = capitalized ? SLOT_NOUN[0].toUpperCase() + SLOT_NOUN.slice(1) : SLOT_NOUN
  return `${noun} ${position}`
}

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ATTENDANCE — A COUNT OVER THE NIGHTS SOMEBODY LOGGED, AND IT SAYS SO
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-173. `nights_attended` under-reports BY CONSTRUCTION: the bot writes an
// attendance list only from `finalize_movie_night` / `record_retro_night`, so a
// night with no `!movie start`, a night under thirty minutes in voice, and
// every night predating attendance tracking contribute nothing. The panel used
// to render that number as a display-size stat beside `PICKED` — a count that
// IS complete, because every history row names a picker — and Jordan read his
// own card as `12 NIGHTS / 13 PICKED` with sixty-odd nights behind him.
//
// ✅ THE PUBLISHER NOW STATES THE GAP RATHER THAN HIDING IT: `nights_attended`
// is `null` when nothing anywhere was logged, and `nights_with_attendance` (3a,
// beside `nights_recorded`) is how many nights were. This function is the only
// place that turns those three numbers into words.
//
// 🔴 THE OLD BLOB IS NOT DEAD CODE. The projection in the bucket right now was
// written before `nights_with_attendance` existed and carries `nights_attended`
// as a hard zero-or-count with no denominator. There is no honest sentence to
// build from that, so this returns `null` and the panel renders nothing —
// "I could not look" is not "I looked and found none", and the alternative is
// to keep printing the bug until the bot next publishes.
//
// ⛔ NOTHING HERE ESTIMATES. The missing nights were never recorded; inventing
// them would put made-up history in front of the people who were actually
// there. The sentence names the gap and stops.

/**
 * What this member's attendance honestly says, or `null` when it says nothing.
 *
 * Four answers, and the first two are the ones that did not exist before:
 *
 * - **`null`** — the blob cannot support a sentence (pre-P6-173 projection, or
 *   a count that is not a number). Render nothing.
 * - **nothing logged** — `nights_with_attendance` is 0. Say that, and say it
 *   about the room rather than about the reader.
 * - **partly logged** — the count, with both totals, so the number cannot be
 *   read as "nights you have been to".
 * - **fully logged** — every night has a list, so the plain sentence is true
 *   and the apparatus would be noise.
 */
export function attendanceNote(mine, shared) {
  const logged = shared?.nights_with_attendance
  if (!Number.isInteger(logged)) return null

  const recorded = Number.isInteger(shared?.nights_recorded) ? shared.nights_recorded : null
  if (logged === 0) {
    return recorded === null || recorded === 0
      ? 'Nobody has logged who was at a movie night yet.'
      : `Nobody has logged who was at any of the ${recorded} nights so far.`
  }

  const attended = mine?.nights_attended
  if (!Number.isInteger(attended)) return null

  const nights = `${logged} night${logged === 1 ? '' : 's'}`
  if (recorded === null || recorded === logged) return `You were at ${attended} of the ${nights}.`
  return `Attendance is logged for ${nights} out of ${recorded}. You were at ${attended} of those.`
}

// ═══════════════════════════════════════════════════════════════════════════
// POSTERS — one place that knows the host and the size
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 THE BLOB CARRIES A PATH, NEVER A URL, AND THAT IS ENFORCED UPSTREAM.
// `tests/test_projection_publisher.py::test_nothing_anywhere_carries_a_tmdb_url_or_a_size`
// scans every blob and fails on `image.tmdb.org`, `http`, `/t/p/`, `w500` or
// `w200` appearing in any `poster_path`, with the message *"the blob carries
// the stored path; the site picks the host and the size"*. This is the site
// picking them, in ONE function.
//
// ⚠️ IT IS ONE FUNCTION ON PURPOSE, and the count is the argument. Measured
// 2026-09-04 — re-measure it rather than trusting this line:
//
//     grep -arn --exclude-dir=__pycache__ image.tmdb.org cogs/movie/
//
//   10 sites under `cogs/movie/`, 9 of them hardcoding `w500` and 1 `w200`
//   (cog.py 7, events.py 1, views.py 1, plus history_view.py's POSTER_BASE)
//
// 🔴 THE BRIEF FOR THIS ROW SAID "NINE SITES, SIX OF THEM w500" AND BOTH HALVES
// WERE WRONG — 6 is `cog.py`'s w500 count on its own. It is a small error and it
// is the one this repo keeps making: a number relayed from a document instead of
// re-derived. Hence the command above rather than only the answer.
//
// ⚠️ `-a` is load bearing: `cogs/movie/__pycache__` holds compiled copies with
// the same strings in them, and grep TRUNCATES on a binary file while exiting 0
// (CLAUDE.md gotcha 1). Excluding the directory is what makes the count the
// SOURCE's; `-a` is what stops a stray artefact silently shortening it.

/** TMDb's image host. Not configurable: a second host here would be a second
 * place to audit for where the browser is being sent. */
const POSTER_HOST = 'https://image.tmdb.org'

/** The path prefix TMDb serves sized images from. */
const POSTER_BASE = `${POSTER_HOST}/t/p/`

/**
 * The size, chosen for the slot rather than copied from the cog.
 *
 * 🔴 THE SLOT IS 104 CSS px WIDE (`.d2d-portal__lead`'s first grid column) at
 * 2:3, so 104×156. A phone renders that at DPR 2 or 3 — 208 or 312 device
 * pixels across — and TMDb's poster ladder is w92, w154, w185, w342, w500,
 * w780, original. **w342 is the smallest rung that covers 312**, so it is the
 * smallest file that is never upscaled in this slot.
 *
 * ⚠️ `w500` — what 9 of the cog's 10 sites use — would ship ~46% more pixels
 * than the slot can show, on the surface most likely to be opened on a phone.
 * The cog's choice is right for a Discord embed, which is much wider; it is not
 * right here, and copying it would have been the easy mistake.
 */
const POSTER_SIZE = 'w342'

/**
 * The image URL for a stored poster path, or `null` when there is none.
 *
 * 🔴 THE ORIGIN IS ASSERTED, NOT PATTERN-MATCHED. `new URL` resolves the value
 * exactly as the browser would before deciding whether the result still points
 * at TMDb, so this cannot be argued past by a shape nobody thought of — the
 * check IS the property (a request to `image.tmdb.org` and nowhere else)
 * rather than a proxy for it.
 *
 * ⚠️ HONESTLY SCOPED: `poster_path` comes from TMDb's own API by way of a
 * bot-owned store, so this is not guarding against a hostile input the way
 * `src/trips/EntryCard.jsx::photoUrl` is — that one renders a URL somebody
 * typed. It is here because the site is the last thing between a stored value
 * and a request made on a member's machine, and three lines is cheap enough
 * that "the store is trusted" does not have to be the reason it is safe.
 */
export function posterUrl(path) {
  if (typeof path !== 'string' || path === '') return null
  let url
  try {
    url = new URL(`${POSTER_SIZE}${path}`, POSTER_BASE)
  } catch {
    return null
  }
  return url.origin === POSTER_HOST ? url.href : null
}
